import type { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import type {
  DemandApi,
  SecondLookOfferConfiguration,
} from '@evenfinancial/originator-client';

import { client } from '@portal/api-client/src/client';
import { getPortalError } from '@portal/common';

import {
  createSecondLookOfferConfigurationAction,
  getDemandApisAction,
  getDemandApisBySubAccountUuidAction,
  getSecondLookOfferConfigurationsBySupplySubAccountIdAction,
} from './actions';

function* getDemandApisSaga({
  payload,
}: ActionType<typeof getDemandApisAction.request>) {
  try {
    // need to clear out old demandApis because they are sometimes filtered
    // by productType
    yield put(getDemandApisAction.success([]));
    const { data: demandApis }: AxiosResponse<DemandApi[]> = yield call(
      [client, 'get'],
      '/originator/demandApis',
      { params: payload }
    );

    yield put(getDemandApisAction.success(demandApis));
  } catch (err: unknown) {
    yield put(getDemandApisAction.failure(err));
  }
}

function* getDemandApisBySubAccountUuidSaga({
  payload: { uuid },
}: ActionType<typeof getDemandApisBySubAccountUuidAction.request>) {
  try {
    const { data: demandApis }: AxiosResponse<DemandApi[]> = yield call(
      [client, 'get'],
      '/originator/demandApis',
      {
        params: { subAccountUuid: uuid },
      }
    );

    yield put(
      getDemandApisBySubAccountUuidAction.success({
        demandApis,
        uuid,
      })
    );
  } catch (err: unknown) {
    yield put(getDemandApisBySubAccountUuidAction.failure(err));
  }
}

export function* demandApiSaga() {}

export function* getSecondLookOfferConfigurationsBySupplySubAccountIdSaga(
  action: ActionType<
    typeof getSecondLookOfferConfigurationsBySupplySubAccountIdAction.request
  >
) {
  try {
    const { supplySubAccountId } = action.payload;
    const {
      data: secondLookOfferConfigurations,
    }: AxiosResponse<SecondLookOfferConfiguration[]> = yield call(
      [client, 'get'],
      '/originator/second-look-offer-configurations',
      { params: { supplySubAccountId } }
    );

    yield put(
      getSecondLookOfferConfigurationsBySupplySubAccountIdAction.success({
        secondLookOfferConfigurations,
        supplySubAccountId,
      })
    );
  } catch (err: unknown) {
    const portalError = getPortalError(err);

    yield put(
      getSecondLookOfferConfigurationsBySupplySubAccountIdAction.failure(
        portalError
      )
    );
  }
}

export function* createSecondLookOfferConfigurationSaga(
  action: ActionType<typeof createSecondLookOfferConfigurationAction.request>
) {
  try {
    yield call(
      [client, 'post'],
      '/originator/second-look-offer-configurations',
      action.payload
    );

    yield put(createSecondLookOfferConfigurationAction.success());
  } catch (err: unknown) {
    const portalError = getPortalError(err);

    yield put(createSecondLookOfferConfigurationAction.failure(portalError));
  }
}

export function* originatorSaga() {
  yield takeLatest(getType(getDemandApisAction.request), getDemandApisSaga);

  yield takeLatest(
    getType(getDemandApisBySubAccountUuidAction.request),
    getDemandApisBySubAccountUuidSaga
  );

  yield takeLatest(
    getType(getSecondLookOfferConfigurationsBySupplySubAccountIdAction.request),
    getSecondLookOfferConfigurationsBySupplySubAccountIdSaga
  );

  yield takeLatest(
    getType(createSecondLookOfferConfigurationAction.request),
    createSecondLookOfferConfigurationSaga
  );
}

export function* originatorExternalSaga() {
  // eslint-disable-next-line redux-saga/no-unhandled-errors
  yield all([fork(originatorSaga)]);
}
